/** 问题清单剩余工作日 / 逾期（对齐 Demo，含 2026 法定节假日） */

const ISSUE_HOLIDAY_DATES = new Set([
  '2026-01-01', '2026-01-02', '2026-01-03',
  '2026-02-15', '2026-02-16', '2026-02-17', '2026-02-18', '2026-02-19', '2026-02-20', '2026-02-21', '2026-02-22', '2026-02-23',
  '2026-04-04', '2026-04-05', '2026-04-06',
  '2026-05-01', '2026-05-02', '2026-05-03', '2026-05-04', '2026-05-05',
  '2026-06-19', '2026-06-20', '2026-06-21',
  '2026-09-25', '2026-09-26', '2026-09-27',
  '2026-10-01', '2026-10-02', '2026-10-03', '2026-10-04', '2026-10-05', '2026-10-06', '2026-10-07',
]);

export type IssueTimeStateCode =
  | 'unset'
  | 'overdue'
  | 'active'
  | 'closed-on-time'
  | 'closed-overdue';

export type IssueTimeState = {
  code: IssueTimeStateCode;
  label: string;
  workdays?: number;
};

function parseIssueDate(value?: string | Date | null): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const normalized = String(value).trim().replace(' ', 'T');
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

function issueDateOnly(value: Date): Date {
  const d = new Date(value);
  d.setHours(0, 0, 0, 0);
  return d;
}

function issueDateValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isIssueWorkday(date: Date): boolean {
  const day = date.getDay();
  return day !== 0 && day !== 6 && !ISSUE_HOLIDAY_DATES.has(issueDateValue(date));
}

export function countIssueWorkdays(startDate: Date, endDate: Date): number {
  const start = issueDateOnly(startDate);
  const end = issueDateOnly(endDate);
  if (start >= end) return 0;
  let count = 0;
  const cursor = new Date(start);
  cursor.setDate(cursor.getDate() + 1);
  while (cursor <= end) {
    if (isIssueWorkday(cursor)) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

export function issueTimeState(opts: {
  dueDate?: string | null;
  closed?: boolean;
  closedAt?: string | null;
  updatedAt?: string | null;
}, referenceDate = new Date()): IssueTimeState {
  const deadline = parseIssueDate(opts.dueDate);
  if (!deadline) return { code: 'unset', label: '未设置截止时间' };
  if (opts.closed) {
    const closedDate = parseIssueDate(opts.closedAt || opts.updatedAt || opts.dueDate);
    const onTime = !closedDate || closedDate <= deadline;
    return { code: onTime ? 'closed-on-time' : 'closed-overdue', label: onTime ? '按期关闭' : '逾期关闭' };
  }
  const now = new Date(referenceDate);
  const today = issueDateOnly(now);
  const deadlineDay = issueDateOnly(deadline);
  if (now > deadline) {
    const overdueDays = countIssueWorkdays(deadlineDay, today);
    return {
      code: 'overdue',
      workdays: overdueDays,
      label: overdueDays === 0 ? '已逾期不足1个工作日' : `已逾期 ${overdueDays} 个工作日`,
    };
  }
  const remainingDays = countIssueWorkdays(today, deadlineDay);
  return {
    code: 'active',
    workdays: remainingDays,
    label: remainingDays === 0 ? '今日截止' : `剩余 ${remainingDays} 个工作日`,
  };
}

import { priorityToRiskLevel, riskLevelToPriority } from '@/utils/riskLevel';

export function priorityToRiskLabel(priority?: string | null): string {
  return priorityToRiskLevel(priority);
}

export function riskLabelToPriority(risk?: string | null): string {
  return riskLevelToPriority(risk);
}

export function formatIssueDateTime(value?: string | null): string {
  const d = parseIssueDate(value);
  if (!d) return '—';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

/** 评论等场景：精确到秒 */
export function formatIssueDateTimeSeconds(value?: string | null): string {
  const d = parseIssueDate(value);
  if (!d) return '—';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${hh}:${mm}:${ss}`;
}

/**
 * 问题截止时间输入统一到分钟（秒位清零）。只用于问题截止时间，不影响预警、评论等秒级时间。
 * 返回值保持 Element Plus datetime picker 的本地时间格式。
 */
export function normalizeIssueDueDateTime(value?: string | Date | null): string | undefined {
  const parsed = parseIssueDate(value ?? null);
  if (!parsed) return undefined;
  const d = new Date(parsed.getTime());
  d.setSeconds(0, 0);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return y + '-' + m + '-' + day + 'T' + hh + ':' + mm + ':00';
}

/** 后端 create/update 用的问题截止时间：yyyy-MM-dd HH:mm:00。 */
export function toIssueDueApiDateTime(value?: string | Date | null): string | undefined {
  return toIssueApiDateTime(normalizeIssueDueDateTime(value));
}

/** 后端 create/update 用：yyyy-MM-dd HH:mm:ss（与 @JsonFormat 一致） */
export function toIssueApiDateTime(value?: string | Date | null): string | undefined {
  const d = parseIssueDate(value ?? null);
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${hh}:${mm}:${ss}`;
}
